
import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Button,
    Grid,
    Container,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
    Chip,
    Stack,
    Divider,
    TextField,
    InputAdornment,
    Fade,
    Slide,
    IconButton,
    Tooltip,
    Badge,
    Avatar,
    LinearProgress,
    Skeleton
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import VerifiedIcon from '@mui/icons-material/Verified';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';
import ColorData from 'Components/CstmTheme/ColorData';
import MobileUserCard from './MobileUserCard';
import AdminApi from 'Service/Admin.api';
import { useDispatch } from 'react-redux';
import { setAlertData, startLoading, stopLoading } from '../../Redux/Screen/Action';
import VerifyProfessionalDialog from './VerifyProfessionalDialog';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
// Animations
const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const slideIn = keyframes`
    from {
        opacity: 0;
        transform: translateX(-20px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
`;

const pulse = keyframes`
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.05);
    }
    100% {
        transform: scale(1);
    }
`;

// Styled Components
export const StyledContainer = styled(Container)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.grey[50]} 100%)`,
    minHeight: '100vh',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
}));

export const HeaderCard = styled(Card)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.spacing(3),
    overflow: 'hidden',
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        width: '200px',
        height: '200px',
        background: `radial-gradient(circle, ${theme.palette.primary.light}30 0%, transparent 70%)`,
        borderRadius: '50%',
        transform: 'translate(50%, -50%)',
    },
    animation: `${fadeIn} 0.8s ease-out`,
}));

const StatsCard = styled(Card)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[8],
        borderColor: theme.palette.primary.main,
    },
    animation: `${slideIn} 0.6s ease-out`,
}));

export const SearchCard = styled(Card)(({ theme }) => ({
    background: theme.palette.background.paper,
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: theme.shadows[4],
    },
}));

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    fontSize: '0.95rem',
    padding: theme.spacing(2),
    borderBottom: 'none',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    '&:first-of-type': {
        borderTopLeftRadius: theme.spacing(1),
    },
    '&:last-of-type': {
        borderTopRightRadius: theme.spacing(1),
    },
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
    // transition: 'all 0.2s ease',
    '&:nth-of-type(even)': {
        backgroundColor: `${theme.palette.action.hover}50`,
    },
    '&:hover': {
        backgroundColor: `${theme.palette.primary.main}08`,
        // transform: 'scale(1.01)',
        boxShadow: `0 4px 12px ${theme.palette.primary.main}20`,
    },
    '& td': {
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1.5, 2),
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.spacing(3),
    textTransform: 'none',
    fontWeight: 600,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[8],
    },
    '&.verified': {
        background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
        '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 100%)`,
        },
    },
    '&.pending': {
        background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
        '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.main} 100%)`,
        },
    },
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    fontWeight: 600,
    fontSize: '0.9rem',
}));

const StatusChip = styled(Chip)(({ theme }) => ({
    fontWeight: 600,
    borderRadius: theme.spacing(3),
    '&.verified': {
        background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
        color: theme.palette.success.contrastText,
        animation: `${pulse} 2s infinite`,
    },
    '&.pending': {
        background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
        color: theme.palette.warning.contrastText,
    },
}));

const LoadingCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(3),
    textAlign: 'center',
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
}));

const ProfessionalUserList = () => {
    const navigate = useNavigate();
    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const theme = useTheme();
    const dispatch = useDispatch();

    // Enhanced responsive breakpoints
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    // Statistics
    const verifiedCount = users.filter((user: any) => user.isVerified).length;

    useEffect(() => {
        fetchListData();
    }, []);

    useEffect(() => {
        if (searchQuery === "") {
            fetchListData();
        } else {
            const filtered = filteredUsers.filter((user: any) => {
                const fullName = `${user.firstName ?? ""} ${user.lastName ?? ""}`.toLowerCase();
                const email = user.email?.toLowerCase() ?? "";
                const contact = user.contactNumber ?? "";

                return (
                    fullName.includes(searchQuery.toLowerCase()) ||
                    email.includes(searchQuery.toLowerCase()) ||
                    contact.includes(searchQuery)
                );
            });

            setFilteredUsers(filtered);
        }
    }, [searchQuery]);

    const fetchListData = async () => {
        try {
            setLoading(true);
            dispatch(startLoading({ loading: true, loadtext: "Fetching Professional User Data..." }));
            const response = await AdminApi.ProfessionalList();

            if (response?.success && Array.isArray(response?.data)) {
                setUsers(response?.data || []);
                setFilteredUsers(response?.data || []);
            }
            else {
                setUsers([]);
                setFilteredUsers([]);
            }
            dispatch(stopLoading());
        } catch (error) {
            console.error("Error fetching data:", error);
            dispatch(
                setAlertData({
                    alertMessage: "Error fetching user data",
                    alertVariant: "error",
                    openAlert: true,
                })
            );
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchListData();
    };

    const handleSelectUser = (userId: number) => {
        setSelectedUsers(prev =>
            prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
        );
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedUsers(filteredUsers.map((user: any) => user.id));
        } else {
            setSelectedUsers([]);
        }
    };

    const handleVerifyUser = async (user: any) => {
        const { email } = user;

    };

    const handleOpenVerifyDialog = (user: any) => {
        console.log("Selected User for Verification:", user);
        setSelectedUser(user);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedUser(null);
    };

    const handleVerifyFromDialog = async () => {

    };


    if (loading && users.length === 0) {
        return (
            <StyledContainer maxWidth="xl">
                <LoadingCard>
                    <LinearProgress sx={{ mb: 2 }} />
                    <Typography variant="h6" color="primary" gutterBottom>
                        Loading Professional Users...
                    </Typography>
                    <Box sx={{ mt: 3 }}>
                        {[...Array(3)].map((_, index) => (
                            <Skeleton
                                key={index}
                                variant="rectangular"
                                height={60}
                                sx={{ mb: 1, borderRadius: 1 }}
                            />
                        ))}
                    </Box>
                </LoadingCard>
            </StyledContainer>
        );
    }

    return (
        <StyledContainer maxWidth="xl">
            <Fade in timeout={800}>
                <Box>
                    {/* 3. ADDED BACK BUTTON HERE */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Button 
                            startIcon={<ArrowBackIcon />} 
                            onClick={() => navigate(-1)}
                            sx={{ fontWeight: 'bold' }}
                        >
                            Back
                        </Button>
                    </Box>
                    {/* Header Section */}
                    <HeaderCard elevation={8} sx={{ mb: 4 }}>
                        <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                flexDirection={isMobile ? 'column' : 'row'}
                                gap={2}
                            >
                                <Box display="flex" alignItems="center" gap={2}>
                                    <VerifiedIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56 } }} />
                                    <Box>
                                        <Typography
                                            variant={isMobile ? "h5" : "h4"}
                                            component="h1"
                                            fontWeight="bold"
                                            color={ColorData.text.secondary}
                                            gutterBottom
                                        >
                                            Professional User Management
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ opacity: 0.9 }}
                                            color='white'
                                        >
                                            Verify and manage professional user accounts with ease
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </HeaderCard>

                    {/* Search and Actions */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <SearchCard elevation={2}>
                                <CardContent>
                                    <TextField
                                        fullWidth
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search professional User..."
                                        variant="outlined"
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon color="primary" />
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 3,
                                                '&:hover fieldset': {
                                                    borderColor: theme.palette.primary.main,
                                                },
                                            },
                                        }}
                                    />
                                </CardContent>
                            </SearchCard>
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box display="flex" gap={2} height="100%" alignItems="center">
                                <Chip
                                    icon={<PersonIcon />}
                                    label={`Total Users: ${users.length}`}
                                    color="info"
                                    variant="outlined"
                                    sx={{
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        height: { xs: 28, sm: 32 }
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Content Section */}
                    <Slide direction="up" in timeout={600}>
                        <Box>
                            {isMobile ? (
                                // Mobile Card View
                                <Box>
                                    {filteredUsers.length === 0 ? (
                                        <Card elevation={2} sx={{ p: 4, textAlign: 'center' }}>
                                            <Typography variant="h6" color={ColorData.text.primary}>
                                                No users found
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                Try adjusting your search criteria
                                            </Typography>
                                        </Card>
                                    ) : (
                                        filteredUsers.map((user: any, index) => (
                                            <MobileUserCard
                                                key={user?.id}
                                                user={user}
                                                index={index}
                                                selectedUsers={selectedUsers}
                                                handleSelectUser={handleSelectUser}
                                                handleVerifyUser={handleVerifyUser}
                                            />
                                        ))
                                    )}
                                </Box>
                            ) : (
                                // Desktop/Tablet Table View
                                <Paper
                                    // elevation={4}
                                    sx={{
                                        borderRadius: 3,
                                        overflow: 'hidden',
                                        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                                    }}
                                >
                                    <TableContainer sx={{ maxHeight: 600, overflow: 'visible' }}>
                                        <Table stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <StyledTableCell>User</StyledTableCell>
                                                    <StyledTableCell>Contact</StyledTableCell>
                                                    <StyledTableCell>Qualification</StyledTableCell>
                                                    <StyledTableCell>Role</StyledTableCell>
                                                    <StyledTableCell>Status</StyledTableCell>
                                                    <StyledTableCell align="center">Actions</StyledTableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {filteredUsers.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                                                            <Typography variant="h6" color={ColorData.text.primary}>
                                                                No users found
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                                Try adjusting your search criteria
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    filteredUsers.map((user: any, index) => (
                                                        <StyledTableRow key={user.id}>
                                                            <TableCell>
                                                                <Box display="flex" alignItems="center" gap={2}>
                                                                    <UserAvatar>
                                                                        {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                                                                    </UserAvatar>
                                                                    <Box>
                                                                        <Typography variant="body1" fontWeight="bold">
                                                                            {user.firstName} {user.lastName}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            ID: {user.id}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Stack spacing={1}>
                                                                    <Box display="flex" alignItems="center" gap={1}>
                                                                        <EmailIcon fontSize="small" color="primary" />
                                                                        <Typography variant="body2">
                                                                            {user.email}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box display="flex" alignItems="center" gap={1}>
                                                                        <PhoneIcon fontSize="small" color="primary" />
                                                                        <Typography variant="body2">
                                                                            {user.contactNumber}
                                                                        </Typography>
                                                                    </Box>
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box display="flex" alignItems="center" gap={1}>
                                                                    <WorkIcon fontSize="small" color="primary" />
                                                                    <Typography variant="body2">
                                                                        {user.highestQualification || 'Not specified'}
                                                                    </Typography>
                                                                </Box>
                                                            </TableCell>
                                                            {/* <TableCell>
                                                                <Typography variant="body2" fontWeight="medium">
                                                                    {user.Roles || 'Professional'}
                                                                </Typography>
                                                            </TableCell> */}
                                                            <TableCell>
                                                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                                                    {(user.roles?.length > 0 ? user.roles : ['No roles assigned']).map((role: string, idx: number) => (
                                                                        <Chip
                                                                            key={idx}
                                                                            label={role}
                                                                            color="info"
                                                                            variant="outlined"
                                                                            size="small"
                                                                        />
                                                                    ))}
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell>
                                                                <StatusChip
                                                                    label={user.isVerified ? 'Verified' : 'Pending'}
                                                                    className={user.isVerified ? 'verified' : 'pending'}
                                                                    icon={user.isVerified ? <VerifiedIcon /> : undefined}
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <ActionButton
                                                                    variant={user.isVerified ? "outlined" : "contained"}
                                                                    size="small"
                                                                    onClick={() => handleOpenVerifyDialog(user)}
                                                                    disabled={loading}
                                                                    startIcon={<VisibilityIcon />}
                                                                    className={user.isVerified ? 'verified' : 'pending'}
                                                                >
                                                                    {user.isVerified ? 'View Details' : 'Verify'}
                                                                </ActionButton>
                                                            </TableCell>
                                                        </StyledTableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            )}
                        </Box>
                    </Slide>
                </Box>
            </Fade>

            {/* Verify Professional Dialog */}
            {selectedUser && (
                <VerifyProfessionalDialog
                    open={dialogOpen}
                    onClose={handleCloseDialog}
                    user={selectedUser}
                    onVerify={handleVerifyFromDialog}
                    fetchListData={fetchListData}
                />
            )}
        </StyledContainer>
    );
};

export default ProfessionalUserList;
